import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Trophy, Star, Calendar, Bell, Target, LifeBuoy } from "lucide-react";

const STEPS = [
  {
    icon: Trophy,
    title: "Welcome to Momentum",
    body: "You're in the company FIFA 2026 league. Predict 72 group-stage matches plus 6 tournament-wide bonus picks. Highest score takes the trophy.",
  },
  {
    icon: Calendar,
    title: "1. Predict every match",
    body: "Head to MATCHES. For each game, pick the outcome (Home / Draw / Away), enter the exact score, and choose if both teams will score. Three guesses = three ways to win.",
  },
  {
    icon: Target,
    title: "2. Scoring · How points work",
    body: "+5 for the exact score · +3 for the right winner · +1 for correct BTTS. Up to 9 points per match. Predictions auto-lock 2 hours before kickoff.",
  },
  {
    icon: Star,
    title: "3. High-stakes bonus picks",
    body: "BONUS tab has 6 tournament-wide questions worth up to 52 extra points: Champion (15), Golden Boot (10), Runner-up (8), Dark Horse (8), Golden Glove (6), Final Goals O/U (5). Dropdowns only — no typos!",
  },
  {
    icon: Bell,
    title: "4. Results & Leaderboard",
    body: "When the admin posts a result, you get an instant in-app notification (bell icon). Check the LEADERBOARD anytime to see who's leading the league.",
  },
  {
    icon: LifeBuoy,
    title: "5. Help & Support",
    body: "For any help or queries reach out to the admin Abdul Basit at abasit@mcorp.ae and cc Customer_Operations_Team_Igaming@mcorp.ae. We'll get you back in the game fast.",
  },
];

export default function OnboardingModal({ open, onClose }) {
  const [step, setStep] = useState(0);

  useEffect(() => { if (open) setStep(0); }, [open]);

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] grid place-items-center p-4 modal-backdrop"
        data-testid="onboarding-modal"
      >
        <motion.div
          initial={{ scale: 0.94, y: 12 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.94, y: 12 }}
          className="relative w-full max-w-lg bg-surface border border-purple/40 rounded-3xl p-8 shadow-[0_30px_80px_rgba(160,64,192,0.35)]"
        >
          <button
            onClick={onClose}
            data-testid="onboarding-close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full grid place-items-center border border-white/10 text-white/60 hover:border-purple hover:text-white transition-colors"
          >
            <X size={16}/>
          </button>

          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-vivid to-purple grid place-items-center mb-5 shadow-[0_0_24px_rgba(160,64,192,0.45)]">
            <Icon size={26} className="text-white"/>
          </div>

          <div className="font-display text-3xl tracking-wide uppercase leading-tight mb-3">{s.title}</div>
          <p className="text-white/70 text-sm leading-relaxed">{s.body}</p>

          {/* Stepper dots */}
          <div className="flex items-center justify-center gap-1.5 mt-6">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                data-testid={`onboarding-step-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-8 bg-lavender" : "w-1.5 bg-white/15 hover:bg-white/30"
                }`}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between mt-7">
            <button
              onClick={onClose}
              data-testid="onboarding-skip"
              className="text-xs tracking-[0.25em] uppercase text-white/40 hover:text-white transition-colors"
            >
              Skip
            </button>
            <button
              onClick={() => isLast ? onClose() : setStep(step + 1)}
              data-testid="onboarding-next"
              className="bg-gradient-to-r from-vivid to-purple text-white font-bold uppercase tracking-wide rounded-full px-6 py-2.5 hover:from-purple hover:to-lavender hover:text-plum transition-all inline-flex items-center gap-2 text-xs"
            >
              {isLast ? "Let's go" : "Next"} <ChevronRight size={14}/>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
